<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { CreateSessionInput, SessionMode } from '../types'
import { useConfigStore } from '@/stores/config'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  NumberField,
  NumberFieldContent,
  NumberFieldDecrement,
  NumberFieldIncrement,
  NumberFieldInput,
} from '@/components/ui/number-field'
import { Label } from '@/components/ui/label'
import { BookOpen, Timer, Dices } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { track } from '@/lib/posthog'

const configStore = useConfigStore()

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{
  'update:open': [value: boolean]
  submit: [input: CreateSessionInput]
}>()

const MIN = 10
const MAX = 100
const STEP = 10

const subject = ref(configStore.subjectOptions[0]?.value ?? '')
const quantity = ref(20)

const filteredSeasonOptions = computed(() =>
  configStore.seasonOptions.filter((opt) => {
    const allowed = configStore.seasonSubjectAvailability[opt.value]
    return !allowed || allowed.includes(subject.value)
  }),
)

const season = ref(filteredSeasonOptions.value[0]?.value ?? '')

watch(subject, () => {
  if (!filteredSeasonOptions.value.some((opt) => opt.value === season.value)) {
    season.value = filteredSeasonOptions.value[0]?.value ?? ''
  }
})

watch(
  () => props.open,
  (val) => {
    if (val) {
      subject.value = configStore.subjectOptions[0]?.value ?? ''
      season.value = filteredSeasonOptions.value[0]?.value ?? ''
      quantity.value = 20
    }
  },
)

function randomize() {
  const subjects = configStore.subjectOptions
  subject.value = subjects[Math.floor(Math.random() * subjects.length)]?.value || ''
  const seasons = filteredSeasonOptions.value
  season.value = seasons[Math.floor(Math.random() * seasons.length)]?.value || ''
  const steps = (MAX - MIN) / STEP + 1
  quantity.value = MIN + Math.floor(Math.random() * steps) * STEP
}

function handleSubmit(mode: SessionMode) {
  if (!subject.value || !season.value) return
  emit('submit', { subject: subject.value, quantity: quantity.value, mode, season: season.value })
  emit('update:open', false)
  track('create_test_completed', {
    subject: subject.value,
    season: season.value,
    quantity: quantity.value,
    mode,
  })
}

function handleOpenChange(val: boolean) {
  emit('update:open', val)
}
</script>

<template>
  <Dialog :open="props.open" @update:open="handleOpenChange">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>New Session</DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-3 py-2">
        <!-- row 0: subject + randomize -->
        <div class="flex items-end gap-2">
          <div class="flex-1 space-y-1.5">
            <Label class="text-xs text-muted-foreground">Subject</Label>
            <Select v-model="subject">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select a subject" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="opt in configStore.subjectOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="icon"
            class="shrink-0"
            title="Randomize"
            @click="randomize"
          >
            <Dices class="h-4 w-4" />
          </Button>
        </div>

        <!-- row 1: season + questions -->
        <div class="flex items-end gap-4 pb-4">
          <div class="flex-1 space-y-1.5">
            <Label class="text-xs text-muted-foreground">Season</Label>
            <Select v-model="season">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Select a season" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="opt in filteredSeasonOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div class="space-y-1.5">
            <Label class="text-xs text-muted-foreground">Questions</Label>
            <NumberField v-model="quantity" :min="MIN" :max="MAX" :step="STEP">
              <NumberFieldContent>
                <NumberFieldDecrement />
                <NumberFieldInput class="w-32" />
                <NumberFieldIncrement />
              </NumberFieldContent>
            </NumberField>
          </div>
        </div>

        <!-- row 2: mode cards -->
        <div class="grid grid-cols-2 gap-3">
          <button
            class="group cursor-pointer rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-left transition-colors hover:bg-amber-500/15 hover:border-amber-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            :disabled="!subject || !season"
            @click="handleSubmit('study')"
          >
            <div class="flex items-center gap-3">
              <BookOpen
                class="h-5 w-5 shrink-0 text-amber-500/60 transition-colors group-hover:text-amber-500/40"
              />
              <div>
                <p class="font-semibold text-sm text-amber-600 dark:text-amber-400">
                  Start Study Session
                </p>
                <p class="text-xs text-muted-foreground leading-snug">Review at your own pace</p>
              </div>
            </div>
          </button>

          <button
            class="group cursor-pointer rounded-lg border border-blue-500/40 bg-blue-500/5 px-4 py-3 text-left transition-colors hover:bg-blue-500/15 hover:border-blue-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            :disabled="!subject || !season"
            @click="handleSubmit('exam')"
          >
            <div class="flex items-center gap-3">
              <Timer
                class="h-5 w-5 shrink-0 text-blue-500/60 transition-colors group-hover:text-blue-500/40"
              />
              <div>
                <p class="font-semibold text-sm text-blue-600 dark:text-blue-400">
                  Start Exam Session
                </p>
                <p class="text-xs text-muted-foreground leading-snug">
                  Complete within a time limit
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
